import { motion, useInView } from 'motion/react';
import { useRef, useState, useEffect } from 'react';
import { Rocket, CheckCircle2, Clock, Shield, Sparkles } from 'lucide-react';

export function TransformationCTA() {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, amount: 0.3 });
  const [timeLeft, setTimeLeft] = useState(23 * 60 + 45); // 23:45 countdown

  useEffect(() => {
    const timer = setInterval(() => {
      setTimeLeft((prev) => (prev > 0 ? prev - 1 : 23 * 60 + 45));
    }, 1000);

    return () => clearInterval(timer);
  }, []);

  const hours = Math.floor(timeLeft / 3600);
  const minutes = Math.floor((timeLeft % 3600) / 60);
  const seconds = timeLeft % 60;

  const benefits = [
    {
      icon: Clock,
      text: 'Setup completo em menos de 24 horas',
      color: '#4ECDC4',
    },
    {
      icon: Shield,
      text: 'Garantia de 30 dias ou dinheiro de volta',
      color: '#10B981',
    },
    {
      icon: Sparkles,
      text: 'Suporte prioritário incluído',
      color: '#FFE66D',
    },
    {
      icon: CheckCircle2,
      text: 'Migração assistida de dados',
      color: '#4ECDC4',
    },
  ];

  return (
    <section ref={ref} className="relative py-24 bg-gradient-to-b from-[#0A0E1A] to-[#0B0E14] overflow-hidden">
      {/* Animated Gradient Background */}
      <div className="absolute inset-0">
        <motion.div
          className="absolute inset-0"
          animate={{
            background: [
              'radial-gradient(circle at 0% 0%, rgba(255, 107, 53, 0.15) 0%, transparent 50%)',
              'radial-gradient(circle at 100% 100%, rgba(78, 205, 196, 0.15) 0%, transparent 50%)',
              'radial-gradient(circle at 0% 100%, rgba(255, 230, 109, 0.15) 0%, transparent 50%)',
              'radial-gradient(circle at 100% 0%, rgba(16, 185, 129, 0.15) 0%, transparent 50%)',
              'radial-gradient(circle at 0% 0%, rgba(255, 107, 53, 0.15) 0%, transparent 50%)',
            ],
          }}
          transition={{ duration: 20, repeat: Infinity }}
        />
      </div>

      {/* Floating particles */}
      <div className="absolute inset-0 overflow-hidden">
        {Array.from({ length: 20 }).map((_, i) => (
          <motion.div
            key={i}
            className="absolute w-2 h-2 rounded-full"
            style={{
              background: `linear-gradient(135deg, ${
                ['#FF6B35', '#4ECDC4', '#FFE66D', '#10B981'][i % 4]
              } 0%, transparent 100%)`,
              left: `${Math.random() * 100}%`,
              top: `${Math.random() * 100}%`,
            }}
            animate={{
              y: [0, -100, 0],
              opacity: [0, 1, 0],
            }}
            transition={{
              duration: 3 + Math.random() * 2,
              repeat: Infinity,
              delay: Math.random() * 2,
            }}
          />
        ))}
      </div>

      <div className="container mx-auto px-6 relative z-10">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
          className="max-w-5xl mx-auto"
        >
          {/* Main Card */}
          <div className="relative p-12 md:p-16 rounded-3xl border-2 border-white/20 bg-gradient-to-br from-white/10 via-[#0B0E14]/90 to-white/5 backdrop-blur-xl overflow-hidden">
            {/* Top Badge */}
            <motion.div
              initial={{ opacity: 0, y: -20 }}
              animate={isInView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="flex justify-center mb-8"
            >
              <div className="inline-flex items-center gap-2 px-6 py-3 rounded-full border border-[#FF6B35]/50 bg-gradient-to-r from-[#FF6B35]/20 to-[#FFE66D]/20 backdrop-blur-sm">
                <Rocket className="w-5 h-5 text-[#FF6B35]" />
                <span className="text-white font-semibold">
                  Oferta Especial - Termina em:
                </span>
                <div className="flex gap-2 ml-2 font-mono">
                  <div className="px-3 py-1 rounded-lg bg-[#FF6B35]/30 text-white font-bold">
                    {String(hours).padStart(2, '0')}
                  </div>
                  <span className="text-white">:</span>
                  <div className="px-3 py-1 rounded-lg bg-[#FF6B35]/30 text-white font-bold">
                    {String(minutes).padStart(2, '0')}
                  </div>
                  <span className="text-white">:</span>
                  <div className="px-3 py-1 rounded-lg bg-[#FF6B35]/30 text-white font-bold">
                    {String(seconds).padStart(2, '0')}
                  </div>
                </div>
              </div>
            </motion.div>

            {/* Main Headline */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={isInView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.6, delay: 0.3 }}
              className="text-center mb-12"
            >
              <h2 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6" style={{ fontFamily: 'Sora, sans-serif' }}>
                <span className="text-white">Inicie Sua</span>
                <br />
                <span className="bg-gradient-to-r from-[#FF6B35] via-[#FFE66D] to-[#4ECDC4] bg-clip-text text-transparent">
                  Transformação Hoje
                </span>
              </h2>
              <p className="text-xl md:text-2xl text-gray-300 max-w-3xl mx-auto">
                Junte-se a <span className="text-[#4ECDC4] font-semibold">1.247 empresas</span> que já 
                saíram do caos para o controle total
              </p>
            </motion.div>

            {/* Benefits Grid */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={isInView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.6, delay: 0.4 }}
              className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-12"
            >
              {benefits.map((benefit, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, x: index % 2 === 0 ? -20 : 20 }}
                  animate={isInView ? { opacity: 1, x: 0 } : {}}
                  transition={{ duration: 0.4, delay: 0.5 + index * 0.1 }}
                  className="flex items-center gap-3 p-4 rounded-xl border border-white/10 bg-white/5 backdrop-blur-sm"
                >
                  <div 
                    className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0"
                    style={{ 
                      background: `linear-gradient(135deg, ${benefit.color}20 0%, ${benefit.color}05 100%)`,
                      border: `1px solid ${benefit.color}30`,
                    }}
                  >
                    <benefit.icon className="w-5 h-5" style={{ color: benefit.color }} />
                  </div>
                  <span className="text-white">{benefit.text}</span>
                </motion.div>
              ))}
            </motion.div>

            {/* Pricing Highlight */}
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={isInView ? { opacity: 1, scale: 1 } : {}}
              transition={{ duration: 0.6, delay: 0.6 }}
              className="text-center mb-12"
            >
              <div className="inline-block">
                <div className="text-gray-400 text-lg mb-2 line-through">De R$ 997/mês</div>
                <div className="flex items-baseline justify-center gap-2 mb-2">
                  <span className="text-5xl md:text-6xl font-bold bg-gradient-to-r from-[#10B981] to-[#4ECDC4] bg-clip-text text-transparent">
                    R$ 497
                  </span>
                  <span className="text-2xl text-gray-400">/mês</span>
                </div>
                <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#10B981]/20 border border-[#10B981]/50">
                  <span className="text-[#10B981] font-semibold">50% OFF</span>
                  <span className="text-gray-300">nos primeiros 3 meses</span>
                </div>
              </div>
            </motion.div>

            {/* CTA Buttons */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={isInView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.6, delay: 0.7 }}
              className="flex flex-col sm:flex-row gap-4 justify-center mb-8"
            >
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className="group relative px-10 py-5 bg-gradient-to-r from-[#FF6B35] to-[#FF8C42] rounded-xl font-bold text-white text-lg shadow-2xl overflow-hidden"
              >
                <span className="relative z-10 flex items-center justify-center gap-2">
                  <Rocket className="w-6 h-6" />
                  Começar Transformação Agora
                </span>
                <motion.div
                  className="absolute inset-0 bg-gradient-to-r from-[#FF8C42] to-[#FF6B35]"
                  initial={{ x: '-100%' }}
                  whileHover={{ x: 0 }}
                  transition={{ duration: 0.3 }}
                />
              </motion.button>

              <motion.button
                whileHover={{ scale: 1.05 }}
                className="px-10 py-5 border-2 border-[#4ECDC4]/50 rounded-xl font-semibold text-[#4ECDC4] text-lg hover:bg-[#4ECDC4]/10 transition-all"
              >
                Agendar Demonstração
              </motion.button>
            </motion.div>

            {/* Trust Badges */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={isInView ? { opacity: 1 } : {}}
              transition={{ duration: 0.6, delay: 0.8 }}
              className="flex flex-wrap justify-center gap-6 text-sm text-gray-400"
            >
              <div className="flex items-center gap-2">
                <CheckCircle2 className="w-4 h-4 text-[#10B981]" />
                <span>Sem compromisso</span>
              </div>
              <div className="flex items-center gap-2">
                <CheckCircle2 className="w-4 h-4 text-[#10B981]" />
                <span>Cancele quando quiser</span>
              </div>
              <div className="flex items-center gap-2">
                <CheckCircle2 className="w-4 h-4 text-[#10B981]" />
                <span>Garantia de 30 dias</span>
              </div>
            </motion.div>

            {/* Glow Effect */}
            <div className="absolute inset-0 rounded-3xl bg-gradient-to-br from-[#FF6B35]/20 via-transparent to-[#4ECDC4]/20 blur-3xl opacity-50 -z-10" />
          </div>

          {/* Social Proof Footer */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={isInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.6, delay: 0.9 }}
            className="text-center mt-12"
          >
            <div className="flex flex-wrap items-center justify-center gap-8 text-gray-400">
              <div className="flex items-center gap-2">
                <div className="flex -space-x-2">
                  {['#FF6B35', '#4ECDC4', '#FFE66D', '#10B981'].map((color, i) => (
                    <div
                      key={i}
                      className="w-10 h-10 rounded-full border-2 border-[#0B0E14] flex items-center justify-center text-white font-bold text-sm"
                      style={{ backgroundColor: color }}
                    >
                      {String.fromCharCode(65 + i)}
                    </div>
                  ))}
                </div>
                <span>342 empresas iniciaram hoje</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="flex">
                  {[1, 2, 3, 4, 5].map((star) => (
                    <span key={star} className="text-[#FFE66D] text-xl">★</span>
                  ))}
                </div>
                <span>4.9/5.0 · 1.247 avaliações</span>
              </div>
            </div>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
}
